/**
 * Session-38 — prefetch cache for chat message payloads.
 *
 * Used by HandlerDashboard's chat list. On `mouseenter` / `touchstart` of a
 * chat row, we start fetching the room's latest 50 messages so that by the
 * time the user's tap resolves and HandlerChat mounts, the payload is either
 * already in flight or complete. Feels instant even on 3G.
 *
 * The consuming component (HandlerChat / AdminChat) can call `takePrefetch`
 * to consume the pending promise before firing its own request. If the
 * prefetch is <5 seconds old we use it; otherwise it's stale and dropped.
 *
 * Safety:
 *   • Prefetches are best-effort — errors are swallowed silently.
 *   • Each entry auto-evicts after 15 seconds so we don't leak memory.
 *   • Never cache anything beyond the initial page — pagination "load more"
 *     always hits the network fresh.
 */

const store = new Map(); // room_id -> { at: epoch, promise: Promise<any> }
const MAX_AGE_MS = 15_000;

function prune() {
  const cutoff = Date.now() - MAX_AGE_MS;
  for (const [k, v] of store.entries()) {
    if (v.at < cutoff) store.delete(k);
  }
}

export function prefetchRoom(roomId, fetcher) {
  if (!roomId || typeof fetcher !== 'function') return;
  prune();
  const existing = store.get(roomId);
  if (existing && Date.now() - existing.at < MAX_AGE_MS) return; // already primed
  const promise = fetcher().catch(() => null);
  store.set(roomId, { at: Date.now(), promise });
}

/** Consume a prefetch. Returns the promise if fresh, else null. */
export function takePrefetch(roomId) {
  const entry = store.get(roomId);
  if (!entry) return null;
  if (Date.now() - entry.at > MAX_AGE_MS) {
    store.delete(roomId);
    return null;
  }
  store.delete(roomId);
  return entry.promise;
}

export function clearPrefetch(roomId) {
  if (roomId) store.delete(roomId);
  else store.clear();
}
